import classNames from 'classnames';
import { type MouseEvent, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import Button from '@alltrails/denali/components/Button';
import Link from '@alltrails/denali/components/Link';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import Play from '@alltrails/denali/icons/Play';
import styles from './FlyoverEntry.module.scss';

export type FlyoverEntryClickSource = 'playButton' | 'previewImage';

type FlyoverEntryProps = {
  buttonContainerClassName?: string;
  className?: string;
  flyoverHref?: string;
  flyoverUnavailable?: boolean;
  hidePlusBadge?: boolean;
  isLoading?: boolean;
  innerClassName?: string;
  onClick?: (source: FlyoverEntryClickSource, e?: MouseEvent) => void;
  routeName: string;
  staticMapSizes?: string;
  staticMapUrl: string;
  titleClassName?: string;
  size?: 'sm' | 'md';
};

const FlyoverEntry = ({
  buttonContainerClassName,
  className,
  flyoverHref,
  flyoverUnavailable = false,
  hidePlusBadge = false,
  isLoading,
  innerClassName,
  onClick,
  routeName,
  staticMapSizes,
  staticMapUrl,
  titleClassName,
  size = 'md'
}: FlyoverEntryProps) => {
  const intl = useIntl();
  const [showOfflineError, setShowOfflineError] = useState(false);

  const onClickInternal = (source: FlyoverEntryClickSource, e?: MouseEvent) => {
    if (isLoading) {
      return;
    }
    onClick?.(source, e);
    setShowOfflineError(false);
    if (!window.navigator.onLine) {
      e?.preventDefault();
      setShowOfflineError(true);
    }
  };

  return (
    <div className={classNames(styles.relativeCard, className)}>
      <Link
        className={styles.flyoverLink}
        href={flyoverUnavailable ? undefined : flyoverHref}
        onClick={e => onClickInternal('previewImage', e)}
      >
        <div className={classNames(styles.flyoverInner, innerClassName)}>
          <div className={classNames(styles.lightTitle, titleClassName)}>
            <FormattedMessage defaultMessage="Preview" />
            {!hidePlusBadge && <PlusLogoBadge variant="full" textColor="light" />}
          </div>
          <Image
            alt={intl.formatMessage(
              { defaultMessage: 'A still from a preview of {name} shows a 3D view of the map with a route partially completed.' },
              { name: routeName }
            )}
            className={styles.staticImage}
            fill
            loading="eager"
            sizes={staticMapSizes}
            src={staticMapUrl}
          />
          <div className={classNames(styles.scrim, styles.bottomScrim)} />
          <div className={classNames(styles.scrim, styles.topScrim)} />
          <div className={styles.mapboxAttribution}>
            <span>© Mapbox</span>
            <span>© Maxar</span>
            <span>© OpenStreetMap</span>
          </div>
        </div>
      </Link>
      <div className={classNames(styles.flyoverContainer, buttonContainerClassName)}>
        {!flyoverUnavailable && (
          <Button
            className={styles.buttonEntry}
            icon={{ Component: Play }}
            linkInfo={flyoverHref ? { href: flyoverHref } : undefined}
            loading={isLoading}
            onClick={e => onClickInternal('playButton', e)}
            text={<FormattedMessage defaultMessage="Play" />}
            size={size}
            variant="elevated"
          />
        )}
        {showOfflineError && (
          <span className={styles.error}>
            <FormattedMessage defaultMessage="We’re unable to submit your request because you’re offline. Please try again later." />
          </span>
        )}
      </div>
    </div>
  );
};

export default FlyoverEntry;
